import { useEffect, useRef, useState } from "react";

const MP_CDN = "https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation@0.1";

/**
 * useVirtualBackground
 * Renders a <video> stream onto a <canvas> with MediaPipe Selfie Segmentation,
 * compositing a chosen background (blur or image) behind the person.
 *
 * Returns { canvasRef, status }.  Set videoRef.current = your <video> element.
 *
 * background.kind: "off" | "blur" | "image"
 * background.imageUrl: only when kind === "image"
 * background.blur: blur radius in px (e.g. 12)
 */
export default function useVirtualBackground({ videoRef, enabled, background }) {
  const canvasRef = useRef(null);
  const segRef = useRef(null);
  const bgImgRef = useRef(null);
  const rafRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error

  // Preload background image when imageUrl changes
  useEffect(() => {
    if (background?.kind !== "image" || !background.imageUrl) {
      bgImgRef.current = null;
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = background.imageUrl;
    img.onload = () => { bgImgRef.current = img; };
    img.onerror = () => { bgImgRef.current = null; };
  }, [background?.kind, background?.imageUrl]);

  useEffect(() => {
    if (!enabled || !videoRef?.current || !canvasRef.current) return;
    if (!window.SelfieSegmentation) {
      setStatus("error");
      return;
    }
    setStatus("loading");

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let cancelled = false;

    const seg = new window.SelfieSegmentation({
      locateFile: (file) => `${MP_CDN}/${file}`,
    });
    seg.setOptions({ modelSelection: 1, selfieMode: true });

    seg.onResults((results) => {
      if (cancelled) return;
      const w = canvas.width = results.image.width;
      const h = canvas.height = results.image.height;

      ctx.save();
      ctx.clearRect(0, 0, w, h);

      // 1. Draw background layer
      if (background.kind === "image" && bgImgRef.current) {
        // cover fit
        const img = bgImgRef.current;
        const ratio = Math.max(w / img.width, h / img.height);
        const iw = img.width * ratio;
        const ih = img.height * ratio;
        ctx.drawImage(img, (w - iw) / 2, (h - ih) / 2, iw, ih);
      } else if (background.kind === "blur") {
        ctx.filter = `blur(${background.blur || 12}px)`;
        ctx.drawImage(results.image, 0, 0, w, h);
        ctx.filter = "none";
      } else {
        // "off" — original frame
        ctx.drawImage(results.image, 0, 0, w, h);
      }

      // 2. Mask + composite person
      if (background.kind !== "off") {
        ctx.globalCompositeOperation = "destination-out";
        // invert mask: keep background, drop where person is
        ctx.drawImage(results.segmentationMask, 0, 0, w, h);
        ctx.globalCompositeOperation = "destination-over";
        ctx.drawImage(results.image, 0, 0, w, h);
      }

      ctx.restore();
    });
    segRef.current = seg;

    const tick = async () => {
      if (cancelled) return;
      if (video.readyState >= 2) {
        try {
          await seg.send({ image: video });
          if (status !== "ready") setStatus("ready");
        } catch (err) {
          // Per-frame send errors happen during teardown / track changes — log once in dev
          if (process.env.NODE_ENV === "development") {
            console.debug("[useVirtualBackground] frame send failed:", err?.message);
          }
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      try {
        if (seg.close) seg.close();
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.debug("[useVirtualBackground] cleanup error:", err?.message);
        }
      }
      segRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, videoRef, background?.kind, background?.blur, background?.imageUrl]);

  return { canvasRef, status };
}
